import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function CreateChallengeDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [challengeType, setChallengeType] = useState("sleep");
  const [habitType, setHabitType] = useState("alcohol_free");
  const [duration, setDuration] = useState("7");
  const [target, setTarget] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [maxParticipants, setMaxParticipants] = useState("");
  const [enableTeams, setEnableTeams] = useState(false);
  const [teamNames, setTeamNames] = useState(["Team A", "Team B"]);
  const [requireProof, setRequireProof] = useState(false);

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(duration));

      const goalCriteria = {
        required_days: parseInt(duration),
        target: target ? parseFloat(target) : undefined,
        description: `Complete ${duration} days`,
        habit_type: challengeType === 'habits' ? habitType : undefined,
      };

      // Generate invite code for private challenges
      const inviteCode = !isPublic ? Math.random().toString(36).substring(2, 10).toUpperCase() : null;

      const { data, error } = await supabase
        .from('group_challenges')
        .insert({
          title,
          description,
          challenge_type: challengeType,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          goal_criteria: goalCriteria,
          is_public: isPublic,
          is_private: !isPublic,
          invite_code: inviteCode,
          require_proof: requireProof,
          max_participants: maxParticipants ? parseInt(maxParticipants) : null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Create teams if enabled
      if (enableTeams && teamNames.length > 0) {
        const teamInserts = teamNames.map(name => ({
          challenge_id: data.id,
          team_name: name,
          team_color: getRandomColor(),
        }));

        await supabase.from('challenge_teams').insert(teamInserts);
      }

      // Automatically join the challenge as creator
      await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: data.id,
          user_id: user.id,
          completion_status: 'active',
        });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['browse-challenges'] });
      queryClient.invalidateQueries({ queryKey: ['active-challenges'] });
      
      if (!isPublic && data.invite_code) {
        toast.success(`Challenge created! Invite code: ${data.invite_code}`);
      } else {
        toast.success("Challenge created!");
      }
      
      setOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create challenge");
    },
  });

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setChallengeType("sleep");
    setHabitType("alcohol_free");
    setDuration("7");
    setTarget("");
    setIsPublic(true);
    setMaxParticipants("");
    setEnableTeams(false);
    setTeamNames(["Team A", "Team B"]);
    setRequireProof(false);
  };

  const getRandomColor = () => {
    const colors = ['hsl(42,34%,73%)', 'hsl(182,37%,46%)', 'hsl(177,41%,76%)', 'hsl(183,92%,51%)', 'hsl(182,29%,26%)', 'hsl(42,34%,83%)'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const addTeam = () => {
    setTeamNames([...teamNames, `Team ${String.fromCharCode(65 + teamNames.length)}`]);
  };

  const removeTeam = (index: number) => {
    if (teamNames.length > 2) {
      setTeamNames(teamNames.filter((_, i) => i !== index));
    }
  };

  const updateTeamName = (index: number, name: string) => {
    const updated = [...teamNames];
    updated[index] = name;
    setTeamNames(updated);
  };

  const getTargetLabel = () => {
    if (challengeType === 'habits') return 'Daily target (optional)';
    switch (challengeType) {
      case 'sleep': return 'Hours per night';
      case 'nutrition': return 'Healthy food score (1-10)';
      case 'goals': return 'Actions per day';
      default: return 'Target value';
    }
  };

  const getTargetPlaceholder = () => {
    if (challengeType === 'habits') return '';
    switch (challengeType) {
      case 'sleep': return '8';
      case 'nutrition': return '7';
      case 'goals': return '2';
      default: return '';
    }
  };

  const getHabitLabel = (habit: string) => {
    const labels: Record<string, string> = {
      'alcohol_free': 'Alcohol-Free Days',
      'no_smoking': 'Smoke-Free Days',
      'water_intake': 'Adequate Water (2L+)',
      'screen_time': 'Limit Screen Time (<2hrs)',
      'gratitude': 'Daily Gratitude Practice',
      'exercise': 'Daily Exercise',
      'sleep_8h': '8+ Hours Sleep',
      'sunlight': 'Morning Sunlight',
    };
    return labels[habit] || habit;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Create Challenge
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Challenge</DialogTitle>
          <DialogDescription>
            Create a challenge for yourself or invite others to join you
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Challenge Title *</Label>
            <Input
              id="title"
              placeholder="e.g., 30-Day Sleep Challenge"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Describe the challenge and what participants should aim for..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Challenge Type *</Label>
              <Select value={challengeType} onValueChange={setChallengeType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="sleep">Sleep</SelectItem>
                  <SelectItem value="medication">Medication</SelectItem>
                  <SelectItem value="nutrition">Nutrition</SelectItem>
                  <SelectItem value="goals">Goals</SelectItem>
                  <SelectItem value="meditation">Meditation</SelectItem>
                  <SelectItem value="breathwork">Breathwork</SelectItem>
                  <SelectItem value="habits">Habits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration (days) *</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="21">21 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {challengeType === 'habits' && (
            <div className="space-y-2">
              <Label htmlFor="habitType">Habit Type *</Label>
              <Select value={habitType} onValueChange={setHabitType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="alcohol_free">{getHabitLabel('alcohol_free')}</SelectItem>
                  <SelectItem value="no_smoking">{getHabitLabel('no_smoking')}</SelectItem>
                  <SelectItem value="water_intake">{getHabitLabel('water_intake')}</SelectItem>
                  <SelectItem value="screen_time">{getHabitLabel('screen_time')}</SelectItem>
                  <SelectItem value="gratitude">{getHabitLabel('gratitude')}</SelectItem>
                  <SelectItem value="exercise">{getHabitLabel('exercise')}</SelectItem>
                  <SelectItem value="sleep_8h">{getHabitLabel('sleep_8h')}</SelectItem>
                  <SelectItem value="sunlight">{getHabitLabel('sunlight')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="target">{getTargetLabel()}</Label>
            <Input
              id="target"
              type="number"
              placeholder={getTargetPlaceholder()}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxParticipants">Max Participants (optional)</Label>
            <Input
              id="maxParticipants"
              type="number"
              placeholder="Leave empty for unlimited"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="public">Public Challenge</Label>
                <p className="text-sm text-muted-foreground">
                  {isPublic ? 'Anyone can discover and join' : 'Invite-only with code'}
                </p>
              </div>
              <Switch
                id="public"
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="teams">Enable Teams</Label>
                <p className="text-sm text-muted-foreground">
                  Participants compete in teams
                </p>
              </div>
              <Switch
                id="teams"
                checked={enableTeams}
                onCheckedChange={setEnableTeams}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="proof">Require Proof</Label>
                <p className="text-sm text-muted-foreground">
                  Users must submit proof for progress
                </p>
              </div>
              <Switch
                id="proof"
                checked={requireProof}
                onCheckedChange={setRequireProof}
              />
            </div>
          </div>

          {enableTeams && (
            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <Label>Team Names</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTeam}>
                  Add Team
                </Button>
              </div>
              {teamNames.map((name, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={name}
                    onChange={(e) => updateTeamName(index, e.target.value)}
                    placeholder={`Team ${index + 1}`}
                  />
                  {teamNames.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTeam(index)}
                    >
                      ✕
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!title || !description || createMutation.isPending}
              className="flex-1"
            >
              Create Challenge
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
